import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui'
import { routeFor } from '../funnel/useFunnel'
import { eventOnce, funnelApi, getAuth, initialSessionId, paymentsApi } from '../lib/api'
import { getCatalog, openInlineCheckout, previewPrices } from '../lib/paddle'
import { clearGiftCode, getGiftCode } from '../lib/giftcode'

// Fallback list/discounted prices (EUR, tax included) shown only if Paddle
// PricePreview is unavailable. Source of truth is Paddle; these mirror the live
// premium prices as a euro-zone buyer sees them.
const FALLBACK = { base: '€59.99', now: '€47.99' }
const benefitKeys = ['experiences', 'rituals', 'memories', 'insights', 'hosting', 'partner'] as const
const PADDLE_FRAME = 'paddle-checkout' // Paddle mounts the inline form into this element class

export function OfferScreen() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const auth = useMemo(() => getAuth(), [])
  // A free-Premium code carried from the QR card (captured on the landing). When present
  // we lead with the free path and bypass Paddle entirely once the account exists.
  const cardCode = useMemo(() => getGiftCode(), [])

  const [mode, setMode] = useState<'free' | 'paid'>(cardCode ? 'free' : 'paid')
  const [phase, setPhase] = useState<'summary' | 'inline'>('summary')
  const [price, setPrice] = useState(FALLBACK)
  const [checkoutReady, setCheckoutReady] = useState(false)
  const [txId, setTxId] = useState('')
  const [busy, setBusy] = useState(false)
  const [showCode, setShowCode] = useState(false)
  const [code, setCode] = useState(cardCode)
  const [error, setError] = useState('')
  const [note, setNote] = useState('')
  const done = useRef(false)
  const opened = useRef(false)

  // Buying/redeeming binds the grant to the account created at /claim. If the visitor
  // deep-links straight here without an account, send them back to claim first.
  useEffect(() => { if (!auth) navigate(routeFor('gate'), { replace: true }) }, [auth, navigate])

  useEffect(() => {
    void eventOnce(initialSessionId, 'offer_viewed').catch(() => {})
    let alive = true
    ;(async () => {
      try {
        const catalog = await getCatalog()
        const [base, discounted] = await Promise.all([
          previewPrices([catalog.premium.priceId]),
          previewPrices([catalog.premium.priceId], catalog.premium.discountId ?? undefined),
        ])
        if (!alive) return
        setPrice((prev) => ({
          base: base[catalog.premium.priceId] ?? prev.base,
          now: discounted[catalog.premium.priceId] ?? prev.now,
        }))
        setCheckoutReady(true)
      } catch {
        // Checkout not available yet (Paddle unapproved / flag off): the free-code and
        // get-app paths still work; surface a soft note on the paid path.
        if (alive) setNote(t('offer.unavailable'))
      }
    })()
    return () => { alive = false }
  }, [t])

  const finish = (event: 'premium_purchased' | 'free_code_redeemed' | 'premium_offer_skipped') => {
    if (done.current) return
    done.current = true
    void funnelApi.event(initialSessionId, event).catch(() => {})
    navigate(routeFor('handoff'), { replace: true })
  }

  // Free path: redeem the gift code straight into a premium grant — no Paddle.
  const redeemCode = async (raw: string) => {
    const value = raw.trim()
    if (value.length < 4) return
    setBusy(true)
    setError('')
    try {
      await paymentsApi.redeemGift(value)
      clearGiftCode()
      setNote(t('offer.redeemed'))
      finish('free_code_redeemed')
    } catch {
      setError(t('offer.redeemError'))
      // A bad/expired card code shouldn't trap the visitor — fall back to the paid offer.
      setMode('paid')
    } finally {
      setBusy(false)
    }
  }

  // Paid path: create the transaction, then mount Paddle's inline form in our page.
  const buy = async () => {
    setBusy(true)
    setError('')
    try {
      void funnelApi.event(initialSessionId, 'premium_checkout_started').catch(() => {})
      const { transactionId } = await paymentsApi.premiumCheckout()
      setTxId(transactionId)
      setPhase('inline')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('offer.redeemError'))
    } finally {
      setBusy(false)
    }
  }

  // Open the inline checkout once the frame element is in the DOM.
  useEffect(() => {
    if (phase !== 'inline' || !txId || opened.current) return
    opened.current = true
    void openInlineCheckout(txId, PADDLE_FRAME, auth?.user.email)
      .then(() => finish('premium_purchased'))
      .catch((e: unknown) => {
        // Closing/leaving the inline form isn't a hard error — let them retry.
        opened.current = false
        setPhase('summary')
        if (e instanceof Error && e.message !== 'Checkout closed') setError(e.message)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, txId])

  if (!auth) return <div className="content-column" />

  const benefitsList = (
    <ul>{benefitKeys.map((key) => <li key={key}>✓ {t(`offer.benefits.${key}`)}</li>)}</ul>
  )

  // ── Free path — QR card includes free Premium ──────────────────────────────
  if (mode === 'free') {
    return <div className="content-column offer-column">
      <section className="offer-card free">
        <span className="offer-sticker">{t('offer.freeBadge')}</span>
        <p className="eyebrow">{t('offer.freeEyebrow')}</p>
        <h1>{t('offer.freeTitle')}</h1>
        <p className="body-copy">{t('offer.freeBody')}</p>
        {benefitsList}
      </section>
      {error && <p className="error-text" role="alert">{error}</p>}
      <Button variant="sunshine" size="lg" block loading={busy} onClick={() => redeemCode(cardCode)}>
        {t('offer.activateFree')} <span aria-hidden>→</span>
      </Button>
      <button type="button" className="text-button centered" onClick={() => finish('premium_offer_skipped')}>
        {t('offer.later')}
      </button>
    </div>
  }

  // ── Paid path — Bonded-framed inline Paddle checkout ───────────────────────
  return <div className="content-column offer-column">
    <section className="offer-card">
      <span className="offer-sticker">{t('offer.save')}</span>
      <p className="eyebrow">{t('offer.eyebrow')}</p>
      <h1>{t('offer.title')}</h1>
      {phase === 'summary' && <p className="body-copy">{t('offer.body')}</p>}
      <div className="offer-price">
        <strong>{price.now}</strong>
        <span className="offer-was">{price.base}</span>
        <span className="offer-once">{t('offer.oneTime')}</span>
      </div>
      {phase === 'summary' && benefitsList}
      <p className="fine-print">{t('offer.taxNote')}</p>
    </section>

    {error && <p className="error-text" role="alert">{error}</p>}
    {note && <p className="fine-print" role="status">{note}</p>}

    {phase === 'inline' ? (
      <>
        <div className={PADDLE_FRAME} aria-label={t('offer.buy')} />
        <button type="button" className="text-button centered" onClick={() => { opened.current = false; setPhase('summary') }}>
          {t('back')}
        </button>
      </>
    ) : (
      <>
        {checkoutReady && (
          <Button variant="sunshine" size="lg" block loading={busy} onClick={buy}>
            {t('offer.buy')} <span aria-hidden>→</span>
          </Button>
        )}

        {showCode ? (
          <div className="code-row">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              autoCapitalize="characters"
              autoComplete="off"
              placeholder={t('offer.codePlaceholder')}
              aria-label={t('offer.haveCode')}
              className="code-input"
            />
            <Button variant="cloud" loading={busy} disabled={code.trim().length < 4} onClick={() => redeemCode(code)}>
              {t('offer.redeem')}
            </Button>
          </div>
        ) : (
          <button type="button" className="text-button centered" onClick={() => setShowCode(true)}>
            {t('offer.haveCode')}
          </button>
        )}

        <button type="button" className="text-button centered" onClick={() => finish('premium_offer_skipped')}>
          {t('offer.later')}
        </button>
        <p className="fine-print">{t('offer.secure')}</p>
      </>
    )}
  </div>
}
